import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, Handshake, Building, LogOut, ShoppingCart, List, User, Gavel,
  Percent, ChartSpline, AlertTriangle, TrendingDown, Euro, Boxes,
  Menu, X, KeyRound, ClipboardList,
} from 'lucide-react';
import { AuthContext } from '../App';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_API_URL;

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  // ── Tema dinámico (colores + logo desde BD) ───────────────────────────
  const { theme, themeReady } = useTheme();
  const headerBg = theme.primary;

  const defaultLogoSrc = `${process.env.PUBLIC_URL}/logo.png`;
  const logoSrc = theme.logoUrl || defaultLogoSrc;

  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // ── ¿El cliente tiene gestión de stock activa? ────────────────────────
  const [stockActivo, setStockActivo] = useState(false);
  const isCliente = !!user && (user.role_id === 5 || user.permisos === 5);

  useEffect(() => {
    if (!isAuthenticated || !isCliente) { setStockActivo(false); return; }
    let cancelado = false;
    fetch(`${API_URL}/api/stock/clientes-disponibles`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => (r.ok ? r.json() : { clientes: [] }))
      .then((d) => { if (!cancelado) setStockActivo((d.clientes || []).length > 0); })
      .catch(() => { if (!cancelado) setStockActivo(false); });
    return () => { cancelado = true; };
  }, [isAuthenticated, isCliente]);

  const closeMenu = () => setMenuOpen(false);

  const handleLogout = () => {
    closeMenu();
    logout();
    navigate('/');
  };

  // ── Ítems de navegación según rol ─────────────────────────────────────
  // Un ítem con `children` es un grupo (se muestra como sección en el menú).
  const getNavItems = () => {
    if (!isAuthenticated || !user) return [];

    // Cliente (permisos === 5)
    if (user.role_id === 5 || user.permisos === 5) {
      const items = [
        { to: '/gestion-clientes',    label: 'Mi perfil',           Icon: User },
        { to: '/gestion-proveedores', label: 'Mi Catálogo',         Icon: Handshake },
        { to: '/analisis',            label: 'Reportes y análisis', Icon: ChartSpline },
        { to: '/mis-api-keys',        label: 'API Keys',            Icon: KeyRound },
        { to: '/operaciones',         label: 'Consumos quirúrgicos', Icon: ClipboardList },
      ];
      if (stockActivo) items.push({ to: '/gestion-stock', label: 'Stock', Icon: Boxes });
      return items;
    }

    // Proveedor (permisos === 4)
    // NOTA: "Reportes y análisis" (/analisis) se oculta a proveedores porque la
    // pantalla listaba TODOS los clientes (no solo los asignados al proveedor) =
    // fuga de datos. Ademas la ruta esta bloqueada por rol en App.js. El fix de
    // fondo (que la API filtre clientes por proveedor) queda pendiente.
    if (user.permisos === 4) {
      return [
        { to: '/gestion-clientes', label: 'Gestión de Pedidos',   Icon: ShoppingCart },
        { to: '/gestion-ofertas',  label: 'Gestión de Ofertas',   Icon: Percent },
        { to: '/licitaciones',     label: 'Licitaciones',         Icon: Gavel },
      ];
    }

    // Administrador (permisos === 1) — agrupado en secciones
    if (user.permisos === 1) {
      return [
        { label: 'Gestión', Icon: Building, children: [
          { to: '/usuarios',              label: 'Usuarios',               Icon: Users },
          { to: '/gestion-clientes',      label: 'Clientes',               Icon: Building },
          { to: '/gestion-proveedores',   label: 'Proveedores',            Icon: Handshake },
          { to: '/gestion-ofertas-admin', label: 'Ofertas',                Icon: Percent },
          { to: '/admin-apis',            label: 'Administración de APIs',  Icon: KeyRound },
        ] },
        { label: 'Pedidos', Icon: ShoppingCart, children: [
          { to: '/gestion-pedidos',  label: 'Gestión de Pedidos', Icon: List },
          { to: '/pedidos/cerrados', label: 'Pedidos Cerrados',   Icon: List },
          { to: '/licitaciones',     label: 'Licitaciones',       Icon: Gavel },
        ] },
        { label: 'Catálogo', Icon: Boxes, children: [
          { to: '/precios',       label: 'Precios', Icon: Euro },
          { to: '/ahorros',       label: 'Ahorros', Icon: TrendingDown },
          { to: '/gestion-stock', label: 'Stock',   Icon: Boxes },
          { to: '/operaciones',   label: 'Consumos quirúrgicos', Icon: ClipboardList },
        ] },
        { label: 'Análisis', Icon: ChartSpline, children: [
          { to: '/analisis',    label: 'Reportes y análisis', Icon: ChartSpline },
          { to: '/incidencias', label: 'Incidencias',         Icon: AlertTriangle },
        ] },
      ];
    }

    return [];
  };
  const navItems = getNavItems();

  // ── Bloquear render hasta que el tema esté listo ──────────────────────
  if (!themeReady) {
    return <header className="shadow-md sticky top-0 z-50 h-16" style={{ backgroundColor: 'transparent' }} />;
  }

  const showNav = isAuthenticated && navItems.length > 0;

  return (
    <header className="shadow-md sticky top-0 z-50" style={{ backgroundColor: headerBg }}>
      <style>{`@keyframes hdrDropIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:none}}`}</style>
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* 3 columnas: hamburguesa · logo centrado · perfil */}
        <div className="grid grid-cols-3 items-center h-16">

          {/* ── Izquierda: botón hamburguesa (todos los enlaces) ────────── */}
          <div className="flex justify-start">
            {showNav && (
              <button
                onClick={() => setMenuOpen((o) => !o)}
                className="text-white p-2 -ml-2 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-2"
                aria-label="Abrir menú de navegación"
                aria-expanded={menuOpen}
              >
                {menuOpen ? <X size={24} /> : <Menu size={24} />}
                <span className="hidden sm:block text-sm font-medium">Menú</span>
              </button>
            )}
          </div>

          {/* ── Centro: logo ─────────────────────────────────────────────── */}
          <div className="flex justify-center">
            <Link to="/" onClick={closeMenu} className="flex-shrink-0">
              <img
                src={logoSrc}
                alt="Logo"
                className="h-10 w-auto max-w-[130px] sm:max-w-[170px] object-contain"
                onError={(e) => { if (e.target.src !== defaultLogoSrc) e.target.src = defaultLogoSrc; }}
              />
            </Link>
          </div>

          {/* ── Derecha: perfil / logout ─────────────────────────────────── */}
          <div className="flex justify-end items-center">
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <div className="flex items-center text-sm font-medium text-white">
                  <span className="mr-2 hidden md:block max-w-[120px] truncate">
                    {user.nombrec || user.username}
                  </span>
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center font-bold border-2 flex-shrink-0"
                    style={{ backgroundColor: '#ffffff', color: headerBg, borderColor: headerBg }}
                  >
                    {user.nombrec ? user.nombrec.charAt(0).toUpperCase() : 'U'}
                  </div>
                </div>
                <button onClick={handleLogout} className="text-white hover:text-red-200 flex items-center" title="Cerrar sesión">
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center justify-center px-4 py-2 bg-white text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-100 transition-colors"
              >
                Iniciar Sesión
              </Link>
            )}
          </div>
        </div>

        {/* ── Menú desplegable (todos los enlaces) ─────────────────────── */}
        {showNav && menuOpen && (
          <>
            {/* overlay para cerrar al hacer clic fuera */}
            <div className="fixed inset-0 top-16 z-30" onClick={closeMenu} />
            <nav
              className="relative z-40 border-t border-white/20 py-4"
              style={{ animation: 'hdrDropIn .18s ease-out' }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1">
                {navItems.map((item) => (
                  item.children ? (
                    <div key={item.label} className="py-1.5">
                      <div className="px-3 pb-1 text-xs font-bold uppercase tracking-wider text-white/60 flex items-center gap-2">
                        <item.Icon size={14} />
                        {item.label}
                      </div>
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={closeMenu}
                          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-white/90 hover:bg-white/10 text-sm transition-colors"
                        >
                          <c.Icon size={16} className="text-white/60" />
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={closeMenu}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-white hover:bg-white/10 text-sm font-medium transition-colors"
                    >
                      <item.Icon size={18} />
                      {item.label}
                    </Link>
                  )
                ))}
              </div>
            </nav>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
